import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useTranslation } from '../../i18n/TranslationContext';
import { useNotification } from '../../hooks/useNotification';
import DashboardService from '../../service';

const formatRowCreatedAt = (value) => {
  if (value == null || value === '') return '-';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toISOString();
};

const CalculatorVehicleTypesPage = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await DashboardService.getGeVehicleTypesAll());
    } catch (error) {
      showError(error.message || t('calculatorVehicleTypesPage.loadError'));
    } finally {
      setLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingSlug(null);
    form.resetFields();
    form.setFieldsValue({ feeUsd: 0, sortOrder: 0, isActive: true });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingSlug(row.slug);
    form.setFieldsValue({
      slug: row.slug,
      label: row.label,
      feeUsd: Number(row.feeUsd) || 0,
      sortOrder: row.sortOrder ?? 0,
      isActive: row.isActive !== false,
    });
    setModalOpen(true);
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      if (editingSlug) {
        await DashboardService.updateGeVehicleType(editingSlug, {
          label: values.label,
          feeUsd: Number(values.feeUsd) || 0,
          sortOrder: Number(values.sortOrder) || 0,
          isActive: values.isActive !== false,
        });
        showSuccess(t('common.update'));
      } else {
        await DashboardService.createGeVehicleType({
          slug: values.slug.trim().toLowerCase(),
          label: values.label.trim(),
          feeUsd: Number(values.feeUsd) || 0,
          sortOrder: Number(values.sortOrder) || 0,
        });
        showSuccess(t('common.create'));
      }
      setModalOpen(false);
      await load();
    } catch (error) {
      if (error.errorFields) return;
      showError(error.message || t('calculatorVehicleTypesPage.saveError'));
    }
  };

  const disableRow = async (slug) => {
    try {
      await DashboardService.deleteGeVehicleType(slug);
      showSuccess(t('calculatorVehicleTypesPage.disabledOk'));
      await load();
    } catch (error) {
      showError(error.message || t('calculatorVehicleTypesPage.saveError'));
    }
  };

  const confirmPermanentRemove = async () => {
    if (!removeTarget) return;
    try {
      await DashboardService.deleteGeVehicleType(removeTarget.slug, { permanent: true });
      showSuccess(t('calculatorVehicleTypesPage.removedOk'));
      setRemoveTarget(null);
      await load();
    } catch (error) {
      showError(error.message || t('calculatorVehicleTypesPage.saveError'));
    }
  };

  const columns = [
    {
      title: t('calculatorVehicleTypesPage.colId'),
      key: 'index',
      width: 72,
      render: (_, __, index) => index + 1,
    },
    {
      title: t('calculatorVehicleTypesPage.colName'),
      dataIndex: 'label',
      key: 'label',
      render: (value, row) => (
        <Flex vertical>
          <Typography.Text strong>{value}</Typography.Text>
          <Typography.Text code type="secondary">{row.slug}</Typography.Text>
        </Flex>
      ),
    },
    {
      title: t('calculatorVehicleTypesPage.colPrice'),
      dataIndex: 'feeUsd',
      key: 'feeUsd',
      align: 'right',
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: t('calculatorVehicleTypesPage.colCreatedAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: formatRowCreatedAt,
    },
    {
      title: t('common.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value) => (
        <Tag color={value !== false ? 'green' : 'default'}>
          {value !== false ? t('calculatorVehicleTypesPage.activeLabel') : t('calculatorVehicleTypesPage.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      align: 'right',
      render: (_, row) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          {row.isActive !== false && (
            <Button type="text" icon={<StopOutlined />} onClick={() => disableRow(row.slug)} />
          )}
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => setRemoveTarget(row)} />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={16} style={{ marginBottom: 24 }}>
        <Flex vertical>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t('calculatorVehicleTypesPage.title')}
          </Typography.Title>
          <Typography.Text type="secondary">{t('calculatorVehicleTypesPage.subtitle')}</Typography.Text>
        </Flex>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} disabled={loading}>
            {t('common.refresh')}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t('calculatorVehicleTypesPage.addType')}
          </Button>
        </Space>
      </Flex>

      <Table
        rowKey="slug"
        loading={loading}
        columns={columns}
        dataSource={rows}
        locale={{
          emptyText: (
            <Flex vertical gap={4}>
              <Typography.Text strong>{t('calculatorVehicleTypesPage.emptyStateTitle')}</Typography.Text>
              <Typography.Text type="secondary">{t('calculatorVehicleTypesPage.emptyStateBody')}</Typography.Text>
            </Flex>
          ),
        }}
      />

      <Modal
        title={editingSlug ? t('common.edit') : t('calculatorVehicleTypesPage.addType')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={save}
        okText={t('common.save')}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {!editingSlug && (
            <Form.Item
              name="slug"
              label={t('calculatorVehicleTypesPage.colSlug')}
              extra={t('calculatorVehicleTypesPage.slugFormatHint')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          )}
          <Form.Item name="label" label={t('calculatorVehicleTypesPage.colLabel')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="feeUsd" label={t('calculatorVehicleTypesPage.colFeeUsd')}>
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sortOrder" label={t('calculatorVehicleTypesPage.colSort')}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          {editingSlug && (
            <Form.Item
              name="isActive"
              label={t('calculatorVehicleTypesPage.activeInCalculator')}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={t('calculatorVehicleTypesPage.confirmRemoveTitle')}
        open={!!removeTarget}
        onCancel={() => setRemoveTarget(null)}
        onOk={confirmPermanentRemove}
        okText={t('calculatorVehicleTypesPage.confirmRemoveAction')}
        okButtonProps={{ danger: true }}
      >
        <Typography.Text>
          {t('calculatorVehicleTypesPage.confirmRemoveBody').replace(/\{slug\}/g, removeTarget?.slug ?? '')}
        </Typography.Text>
      </Modal>
    </div>
  );
};

export default CalculatorVehicleTypesPage;
